import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../../contexts/AuthContext';
import './RegisterModal.css';

const RegisterModal = ({ isOpen, onClose }) => {
  const { register: registerUser, loading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selfieFile, setSelfieFile] = useState(null);
  const [idFile, setIdFile] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
    clearErrors
  } = useForm();

  const password = watch('password');

  const onSelfieDrop = (files) => files.length && setSelfieFile(files[0]);
  const onIdDrop = (files) => files.length && setIdFile(files[0]);

  const { getRootProps: getSelfieRootProps, getInputProps: getSelfieInputProps } = useDropzone({
    onDrop: onSelfieDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png'] },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
  });

  const { getRootProps: getIdRootProps, getInputProps: getIdInputProps } = useDropzone({
    onDrop: onIdDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png'], 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  const onSubmit = async (data) => {
    clearErrors();
    if (!selfieFile) return setError('selfie', { type: 'manual', message: 'Please upload document to continue' });
    if (!idFile) return setError('idDocument', { type: 'manual', message: 'Please upload document to continue' });

    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => formData.append(key, value));
    formData.append('selfie', selfieFile);
    formData.append('idDocument', idFile);

    const result = await registerUser(formData);
    if (!result.success) setError('root', { type: 'manual', message: result.error });
    else onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="register-modal-overlay" onClick={onClose}>
      <div className="register-modal" onClick={(e) => e.stopPropagation()}>
        <button className="register-modal-close" onClick={onClose}>×</button>
        
        <div className="register-modal-content">
          {/* Left Side */}
          <div className="register-modal-left" style={{
            backgroundImage: "url('/golf-player.png')"
          }}></div>

          {/* Right Side */}
          <div className="register-modal-right">
            {/* Logo */}
            <div className="login-modal-logo">
              <img src="/logo.png" alt="PROSKINS Logo" className="login-modal-logo-img" />
            </div>

            <h2 className="register-modal-title">CREATE AN ACCOUNT</h2>

            {errors.root && (
              <div className="register-modal-error">
                <span>❌</span>
                <div>
                  <strong>Registration failed</strong>
                  <p>{errors.root.message}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="register-modal-form">

              {/* Name Row */}
              <div className="register-modal-row">
                <div className="input-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    {...register('firstName', {required: 'First name is required', minLength: 2})}
                    className={errors.firstName ? 'input-error' : ''}
                  />
                </div>
                <div className="input-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    {...register('lastName', {required: 'Last name is required', minLength: 2})}
                    className={errors.lastName ? 'input-error' : ''}
                  />
                </div>
              </div>

              {/* Contact Row */}
              <div className="register-modal-row">
                <div className="input-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    {...register('phone', {required: 'Phone number is required', pattern: /^[\+]?[1-9][\d]{0,15}$/})}
                    className={errors.phone ? 'input-error' : ''}
                  />
                </div>
                <div className="input-group">
                  <label>Email</label>
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                    })}
                    className={errors.email ? 'input-error' : ''}
                  />
                </div>
              </div>

              {/* Address Row */}
              <div className="register-modal-row">
                <div className="input-group">
                  <label>Address 1</label>
                  <input type="text" {...register('address1')} />
                </div>
                <div className="input-group">
                  <label>Address 2</label>
                  <input type="text" {...register('address2')} />
                </div>
              </div>

              {/* City/State/ZIP Row */}
              <div className="register-modal-row-3">
                <div className="input-group">
                  <label>City</label>
                  <input type="text" {...register('city')} />
                </div>
                <div className="input-group">
                  <label>State</label>
                  <input type="text" {...register('state')} />
                </div>
                <div className="input-group">
                  <label>ZIP</label>
                  <input type="text" {...register('zip')} />
                </div>
              </div>

              {/* Password Row */}
              <div className="register-modal-row">
                <div className="input-group auth-password-container">
                  <label>Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {required: 'Password required', minLength: 8})}
                    className={errors.password ? 'input-error' : ''}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? '👁️‍🗨️' : '👁️'}
                  </button>
                </div>

                <div className="input-group auth-password-container">
                  <label>Confirm Password</label>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...register('confirmPassword', {validate: val => val === password || 'Passwords do not match'})}
                    className={errors.confirmPassword ? 'input-error' : ''}
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? '👁️‍🗨️' : '👁️'}
                  </button>
                </div>
              </div>

              {/* File Upload Row */}
              <div className="register-modal-row">
                {/* ID Document Upload */}
                <div className="upload-container">
                  <div {...getIdRootProps({onClick: () => clearErrors("idDocument")})}
                       className={`upload-box ${idFile ? 'uploaded' : ''}`}>
                    {idFile ? (
                      <div>
                        <strong>✅ License uploaded</strong>
                        <p className="file-name">({idFile.name})</p>
                      </div>
                    ) : (
                      'Upload License'
                    )}
                  </div>
                  <input {...getIdInputProps()} style={{display: 'none'}}/>

                  {/* Validation error */}
                  {errors.idDocument && <p className="uploaderror input-error-text">{errors.idDocument.message}</p>}
                </div>

                {/* Selfie Upload */}
                <div className="upload-container">
                  <div {...getSelfieRootProps({onClick: () => clearErrors("selfie")})}
                       className={`upload-box ${selfieFile ? 'uploaded' : ''}`}>
                    {selfieFile ? (
                      <div>
                        <strong>✅ Photo uploaded</strong>
                        <p className="file-name">({selfieFile.name})</p>
                      </div>
                    ) : (
                      'Upload Photo'
                    )}
                  </div>
                  <input {...getSelfieInputProps()} style={{display: 'none'}}/>

                  {/* Validation error */}
                  {errors.selfie && <p className="uploaderror input-error-text">{errors.selfie.message}</p>}
                </div>
              </div>

              <div className="register-modal-row">
                <label className="proskins-checkbox">
                  <input
                    type="checkbox"
                    {...register('contactConsent', {required: 'You need to agree before continuing.'})}
                    className={errors.contactConsent ? 'input-error' : ''}
                  />
                  <span className="required-text">*</span>
                  <span>I give consent to email or text me about any order or account updates related to this website. I understand SMS standard messaging rates apply.</span>
                </label>
              </div>

              <button type="submit" className="register-modal-submit" disabled={loading}>
                {loading ? 'Creating Account...' : 'CREATE ACCOUNT'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterModal;
